/**
 * MAHI — axios client.
 * All MAHI requests are sent with the JWT we store under `mira_token`.
 */
import axios from 'axios';
import { getSessionId } from './session';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const mahi = axios.create({
  baseURL: `${API_BASE}/api/mira`,
  timeout: 30000,
});

mahi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('mira_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

mahi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('mira_token');
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default mahi;

/** Coerce any backend error payload into a single user-readable string. */
export const errMsg = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d.length) return d.map((x) => x?.msg || x?.message || 'Invalid input').join(' · ');
  if (d && typeof d === 'object') return d.msg || d.message || fallback;
  return e?.message || fallback;
};

/**
 * Persist a swipe. Fire-and-forget: the card has already animated away by the
 * time this resolves, so a failed write must never surface as an error or
 * block the gesture. The signal matters over months — it is what lets MAHI
 * learn someone's taste — so it is worth recording even though nothing in the
 * current screen reads it back.
 */
export const recordSwipe = (miraSku, action, source = 'discovery') => {
  if (!localStorage.getItem('mira_token')) return;
  mahi.post('/swipes', { mira_sku: miraSku, action, source, session_id: getSessionId() }).catch(() => {});
};

/** Retract the most recent swipe for a product (the feed's undo button). */
export const undoSwipe = (miraSku) => {
  if (!localStorage.getItem('mira_token')) return;
  mahi.delete(`/swipes/${encodeURIComponent(miraSku)}`).catch(() => {});
};

/**
 * Fire-and-forget analytics event — never blocks the user flow.
 *
 * `session_id`/`surface`/`product_id`/`brand_id` are the envelope columns
 * (product doc Section 4) that `/api/mira/events` promotes out of `payload`
 * so they're queryable without JSON-parsing every row; everything else
 * event-specific still goes in `payload`. device/app_version are never sent
 * from here — the backend derives them from the request itself (see
 * services/mira/analytics.py::device_from_headers), so a client can't
 * misreport its own platform.
 */
export const trackEvent = (name, payload = {}, { sessionId, surface, productId, brandId } = {}) => {
  if (!localStorage.getItem('mira_token')) return;
  mahi.post('/events', {
    name,
    payload,
    session_id: sessionId,
    surface,
    product_id: productId,
    brand_id: brandId,
  }).catch(() => {});
};

/**
 * Where a "Buy Now" should actually point.
 *
 * The backend mints a signed `buy_url` onto every product it serves — a
 * `/r/{token}` link that records the click, resolves the brand, applies
 * affiliate parameters and then 302s to the merchant. Every buy button in the
 * app was linking straight to `product_url` instead, so that whole path was
 * dark: no click ledger, no brand attribution, and nothing to populate Almost
 * Yours with.
 *
 * Falls back to the raw merchant URL when no token is present. That case is
 * real, not defensive padding: `buy_url` needs a signing key from Secrets
 * Manager, so local dev without one still has to be able to reach the product.
 * A shopper who cannot buy is a worse outcome than a click we failed to bill.
 */
export const buyLink = (product) => product?.buy_url || product?.product_url || '';

/**
 * Record that someone set off to buy this, then let the navigation happen.
 *
 * Deliberately not awaited. The anchor's default action opens the merchant in
 * a new tab immediately; holding that behind a round trip would make the one
 * moment the shopper is most committed feel like the slowest. The redirect
 * itself writes the billing-grade record — this second write is the product
 * state that moves the item into Almost Yours, and losing it costs a row in a
 * list, not revenue.
 */
export const recordBuyIntent = (miraSku, source = 'discovery') => {
  if (!miraSku || !localStorage.getItem('mira_token')) return;
  mahi.post(`/wardrobe/${encodeURIComponent(miraSku)}/buy-intent`, { source })
    .catch(() => {});
};

/**
 * The next slice of a curated deck — deeper into the same search first, then
 * progressively broader.
 *
 * A search deck used to be whatever one tool call returned: eight products,
 * gone in eight swipes, followed by "You're all caught up" on a catalogue of
 * 1,500. That message was true about the filter and false about the shop.
 *
 * `exclude` is everything the deck has already put on screen and `tier` is
 * which rung of the widening ladder it is on (see backend widen.py) — both
 * round-trip through here so the route stays stateless. A POST for a read,
 * because `exclude` grows with every card and would eventually outgrow a query
 * string.
 *
 * Resolves to `{ items, tier, message, exhausted }`, or null on any failure:
 * the deck the shopper already has is still swipeable, and a toast about a
 * background top-up they never asked for is noise.
 */
export const fetchMoreDeck = async ({ conversationId, categories = [], occasions = [], exclude = [], tier = 0 }) => {
  try {
    const { data } = await mahi.post('/inventory/more', {
      conversation_id: conversationId || null,
      categories,
      occasions,
      exclude,
      tier,
    });
    return data;
  } catch {
    return null;
  }
};
